import { api } from "@/lib/api";

// Status is now the workflow_stages.key for the task's project.
// System keys: backlog, todo, in-progress, qa, done, cancelled.
// Custom keys are slugified from user-defined stage names.
export type ApiTaskStatus = string;
export type ApiTaskPriority = "low" | "medium" | "high" | "urgent";

export interface TaskProfile {
	id: string;
	full_name: string | null;
	email: string;
	avatar_url: string | null;
}

export interface TaskSprint {
	id: string;
	name: string;
	status: string;
	start_date: string | null;
	end_date: string | null;
}

export interface TaskTypeRef {
	id: string;
	key: string;
	name: string;
	color: string;
	icon: string;
}

export type PrState = "none" | "open" | "draft" | "merged" | "closed";

// Summary embedded on task reads (board/list). Full detail comes from the
// task-github endpoint.
export interface TaskGithubSummary {
	branch_name: string;
	pr_state: PrState;
	pr_url: string | null;
	pr_number: number | null;
}

export interface Task {
	id: string;
	project_id: string;
	board_column_id: string | null;
	ticket_id: string | null;
	ticket: { ticket_code: string } | null;
	title: string;
	description: string | null;
	developer_notes: string | null;
	status: ApiTaskStatus;
	sub_status: string | null;
	priority: ApiTaskPriority;
	assigned_to: TaskProfile | null;
	additional_assignees: TaskProfile[];
	created_by: TaskProfile;
	due_date: string | null;
	tags: string[];
	position: number;
	sprint_id?: string | null;
	sprint?: TaskSprint | null;
	estimated_time?: number;
	// Estimate recorded for the active sprint (task_sprint_estimates), used by
	// the Estimation display. Only populated by the task-detail endpoint.
	active_sprint_estimated_time?: number | null;
	parent_task_id: string | null;
	parent_task?: {
		id: string;
		title: string;
		status: ApiTaskStatus;
		ticket_id: string | null;
	} | null;
	task_type_id: string | null;
	task_type: TaskTypeRef | null;
	has_calendar_event?: boolean;
	// Checklist aggregates attached by list endpoints; absent when no items.
	checklist_total?: number;
	checklist_done?: number;
	// One-to-one via UNIQUE(task_id); PostgREST may return an object or a
	// single-element array depending on relationship detection.
	github?: TaskGithubSummary | TaskGithubSummary[] | null;
	created_at: string;
	updated_at: string;
}

export interface CreateTaskPayload {
	title: string;
	description?: string;
	status?: ApiTaskStatus;
	sub_status?: string | null;
	priority?: ApiTaskPriority;
	assigned_to?: string;
	assignee_ids?: string[];
	due_date?: string;
	tags?: string[];
	project_id?: string;
	sprint_id?: string;
	estimated_time?: number; // in minutes
	parent_task_id?: string;
	task_type_id?: string | null;
}

export interface UpdateTaskPayload {
	title?: string;
	description?: string;
	developer_notes?: string;
	status?: ApiTaskStatus;
	sub_status?: string | null;
	priority?: ApiTaskPriority;
	assigned_to?: string | null;
	assignee_ids?: string[];
	due_date?: string | null;
	tags?: string[];
	project_id?: string;
	sprint_id?: string | null;
	estimated_time?: number; // in minutes
	parent_task_id?: string | null;
	task_type_id?: string | null;
	// Optimistic concurrency: the updated_at the client last loaded. When the
	// server row has changed since, the update is rejected with a 409 conflict.
	expected_updated_at?: string;
}

export interface TaskConflictError extends Error {
	status: 409;
	code: "TASK_CONFLICT";
	data: Task;
}

export function isTaskConflict(e: unknown): e is TaskConflictError {
	return (
		e instanceof Error &&
		(e as { status?: number }).status === 409 &&
		(e as { code?: string }).code === "TASK_CONFLICT" &&
		!!(e as { data?: unknown }).data
	);
}

export interface ListAllTasksParams {
	id?: string;
	project_id?: string;
	sprint_id?: string;
	parent_task_id?: string;
	status?: ApiTaskStatus;
	// Comma-separated status keys to leave out, e.g. "done,cancelled".
	exclude_status?: string;
	priority?: ApiTaskPriority;
	assigned_to?: string;
	search?: string;
	page?: number;
	limit?: number;
}

export interface TaskPage {
	data: Task[];
	total: number;
}

function tasksUrl(params?: ListAllTasksParams): string {
	const query = new URLSearchParams();
	for (const [k, v] of Object.entries(params ?? {})) {
		if (v !== undefined && v !== "") query.set(k, String(v));
	}
	const qs = query.toString();
	return `/tasks${qs ? `?${qs}` : ""}`;
}

export async function listAllTasks(
	params?: ListAllTasksParams,
): Promise<Task[]> {
	const res = await api.get<{ success: boolean; data: Task[] }>(
		tasksUrl(params),
	);
	return res.data;
}

// Paginated variant: the server returns `total` alongside the page.
export async function listTasksPage(
	params: ListAllTasksParams & { page: number; limit: number },
): Promise<TaskPage> {
	const res = await api.get<{ success: boolean; data: Task[]; total: number }>(
		tasksUrl(params),
	);
	return { data: res.data, total: res.total };
}

export async function listTasks(
	projectId: string,
	params?: { status?: string; priority?: string; assigned_to?: string },
): Promise<Task[]> {
	const query = new URLSearchParams();
	if (params?.status) query.set("status", params.status);
	if (params?.priority) query.set("priority", params.priority);
	if (params?.assigned_to) query.set("assigned_to", params.assigned_to);
	const qs = query.toString();
	const res = await api.get<{ success: boolean; data: Task[] }>(
		`/projects/${projectId}/tasks${qs ? `?${qs}` : ""}`,
	);
	return res.data;
}

export async function createTask(
	projectId: string,
	payload: CreateTaskPayload,
): Promise<Task> {
	const res = await api.post<{ success: boolean; data: Task }>(
		`/projects/${projectId}/tasks`,
		payload,
	);
	return res.data;
}
export async function getTaskDetails(
	projectId: string,
	taskId: string,
): Promise<Task> {
	const res = await api.get<{ success: boolean; data: Task }>(
		`/projects/${projectId}/tasks/${taskId}`,
	);
	return res.data;
}

// Resolve a task by id without knowing its project: look it up by id to read
// project_id, then fetch the full detail through the project route.
export async function getTaskDetailsFromTaskID(taskId: string): Promise<Task> {
	const [match] = await listAllTasks({ id: taskId, limit: 1 });
	if (!match) throw new Error("Task not found.");
	return getTaskDetails(match.project_id, taskId);
}

export async function updateTask(
	projectId: string,
	taskId: string,
	payload: UpdateTaskPayload,
): Promise<Task> {
	const res = await api.patch<{ success: boolean; data: Task }>(
		`/projects/${projectId}/tasks/${taskId}`,
		payload,
	);
	return res.data;
}

export async function deleteTask(
	projectId: string,
	taskId: string,
): Promise<void> {
	await api.delete(`/projects/${projectId}/tasks/${taskId}`);
}
